import { useRef, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { IB_SEVERITIES, IB_MODULE_AREAS } from "../lib/fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Bug, Lightbulb, Sparkles, Upload, X, ImagePlus } from "lucide-react";

const TYPES = [
  { value: "Bug", icon: Bug, color: "text-red-600" },
  { value: "Idea", icon: Lightbulb, color: "text-amber-500" },
  { value: "Feature Request", icon: Sparkles, color: "text-blue-600" },
];

export function ReportBugDialog({ open, onOpenChange, onCreated }) {
  const { t } = useApp();
  const [type, setType] = useState("Bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [moduleArea, setModuleArea] = useState("Other");
  const [severity, setSeverity] = useState("Major");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setType("Bug"); setTitle(""); setDescription(""); setModuleArea("Other");
    setSeverity("Major"); setFiles([]);
  };

  const addFiles = (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/") || f.type === "application/pdf");
    setFiles((prev) => [...prev, ...imgs].slice(0, 5));
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgs = items.filter((i) => i.type.startsWith("image/")).map((i) => i.getAsFile()).filter(Boolean);
    if (imgs.length) { addFiles(imgs); toast.success(t("ib_screenshot_added")); }
  };

  const submit = async () => {
    if (!title.trim() || !description.trim()) { toast.error(t("ib_fill_required")); return; }
    setSaving(true);
    try {
      const r = await api.post("/ideas", {
        type, title, description, module_area: moduleArea,
        severity: type === "Bug" ? severity : null,
      });
      const created = r.data;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/files/ideas/${created.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e) { /* non-fatal */ }
      }
      toast.success(`${created.ib_number} ${t("ib_created")}`);
      reset();
      onOpenChange(false);
      onCreated?.(created);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="report-bug-dialog">
        <DialogHeader>
          <DialogTitle>{t("ib_report_title")}</DialogTitle>
          <DialogDescription>{t("ib_report_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2" data-testid="ib-type-radio">
            {TYPES.map(({ value, icon: Icon, color }) => (
              <button key={value} type="button" data-testid={`ib-type-${value.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setType(value)}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs font-medium transition-colors duration-150 ${
                  type === value ? "border-[#1B2A4A] bg-[#1B2A4A]/5 text-[#1B2A4A]" : "border-gray-200 text-gray-500 hover:border-gray-400"}`}>
                <Icon className={`h-5 w-5 ${type === value ? color : "text-gray-400"}`} />
                {t(`ib_type_${value.replace(/\s+/g, "_").toLowerCase()}`)}
              </button>
            ))}
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t("ib_title_field")} *</Label>
            <Input data-testid="ib-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={t("ib_title_ph")} className="h-9" />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t("ib_description")} *</Label>
            <Textarea data-testid="ib-description-input" rows={4} value={description} onPaste={onPaste}
              onChange={(e) => setDescription(e.target.value)} placeholder={t("ib_description_ph")} />
          </div>

          <div className={`grid ${type === "Bug" ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
            <div className="space-y-1">
              <Label className="text-xs text-gray-600">{t("ib_module_area")}</Label>
              <select data-testid="ib-module-select" value={moduleArea} onChange={(e) => setModuleArea(e.target.value)}
                className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700">
                {IB_MODULE_AREAS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {type === "Bug" && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">{t("severity")}</Label>
                <select data-testid="ib-severity-select" value={severity} onChange={(e) => setSeverity(e.target.value)}
                  className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700">
                  {IB_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>

          <div
            data-testid="ib-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className={`rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
              dragOver ? "border-[#F5A623] bg-amber-50" : "border-gray-200 hover:border-gray-300"}`}>
            <ImagePlus className="h-5 w-5 mx-auto text-gray-400 mb-1" />
            <p className="text-xs text-gray-500">{t("ib_dropzone")}</p>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
              onChange={(e) => addFiles(e.target.files)} />
          </div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="ib-attachments-preview">
              {files.map((f, i) => (
                <div key={i} className="relative">
                  {f.type.startsWith("image/") ? (
                    <img src={URL.createObjectURL(f)} alt="" className="h-14 w-14 object-cover rounded-md border" />
                  ) : (
                    <div className="h-14 w-14 flex items-center justify-center rounded-md border bg-gray-50 text-[9px] text-gray-500 px-1 text-center break-all">{f.name}</div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setFiles((p) => p.filter((_, j) => j !== i)); }}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>{t("cancel")}</Button>
            <Button data-testid="ib-submit-btn" disabled={saving} onClick={submit}
              className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold">
              <Upload className="h-4 w-4 mr-1.5" />{saving ? t("loading") : t("ib_submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
